import { useCallback } from "react";
import type { ParamMeta } from "../engine/scriptRunner";

interface ParamPanelProps {
  params: ParamMeta[];
  values: Record<string, number>;
  onChange: (name: string, value: number) => void;
}

export default function ParamPanel({ params, values, onChange }: ParamPanelProps) {
  if (params.length === 0) return null;

  return (
    <div className="param-panel">
      <div className="param-panel-header">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor">
          <path d="M2 4h3M9 4h5M2 8h7M13 8h1M2 12h1M7 12h7" />
          <circle cx="7" cy="4" r="1.5" />
          <circle cx="11" cy="8" r="1.5" />
          <circle cx="5" cy="12" r="1.5" />
        </svg>
        Parameters
      </div>
      {params.map((p) => (
        <ParamRow key={p.name} meta={p} value={values[p.name] ?? p.value} onChange={onChange} />
      ))}
    </div>
  );
}

function ParamRow({
  meta,
  value,
  onChange,
}: {
  meta: ParamMeta;
  value: number;
  onChange: (name: string, value: number) => void;
}) {
  const step = meta.step ?? (meta.max !== undefined && meta.min !== undefined
    ? (meta.max - meta.min) / 100
    : 1);

  const handleSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(meta.name, parseFloat(e.target.value));
    },
    [meta.name, onChange]
  );

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) onChange(meta.name, v);
    },
    [meta.name, onChange]
  );

  return (
    <div className="param-row">
      <label className="param-label">{meta.name}</label>
      {meta.min !== undefined && meta.max !== undefined && (
        <input
          type="range"
          className="param-slider"
          min={meta.min}
          max={meta.max}
          step={step}
          value={value}
          onChange={handleSlider}
        />
      )}
      <input
        type="number"
        className="param-input"
        min={meta.min}
        max={meta.max}
        step={step}
        value={value}
        onChange={handleInput}
      />
    </div>
  );
}
